import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Space, Tag, Modal, Divider, Spin, Empty, message } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { DownloadOutlined, EyeOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import useUrlFilters from 'hooks/useUrlFilters';

const { Option } = Select;

// Year filter lives in the URL (prefixed, as this renders inside the employee detail page).
const URL_FILTERS = {
  year: { type: 'string', default: 'all' },
};

const EmployeePayslips = ({ employeeId, urlPrefix = 'ps_' }) => {
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS, { prefix: urlPrefix });
  const selectedYear = urlFilters.year === 'all' ? 'all' : Number(urlFilters.year);
  const setSelectedYear = (value) => setUrlFilters({ year: value });
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [payslipData, setPayslipData] = useState([]);
  const [allPayslipData, setAllPayslipData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);

  // Load payslips when employeeId changes
  useEffect(() => {
    if (employeeId) {
      loadPayslips();
    }
  }, [employeeId]);

  // Filter data when year changes
  useEffect(() => {
    if (selectedYear === 'all') {
      setPayslipData(allPayslipData);
    } else {
      setPayslipData(allPayslipData.filter(p => p.month_year.startsWith(selectedYear.toString())));
    }
  }, [selectedYear, allPayslipData]);

  const loadPayslips = async () => {
    if (!employeeId) {
      console.log("No employeeId provided");
      return;
    }
    setLoading(true);
    try {
      const url = `/payroll/payroll-histories/?employee=${employeeId}&page_size=${MAX_PAGE_SIZE}`;
      console.log("Loading payslips from:", url);
      const response = await axios.get(url);
      console.log("Payslips response:", response.data);
      if (response.status === 200) {
        const data = response.data.results || response.data || [];
        const dataArray = Array.isArray(data) ? data : [];
        setAllPayslipData(dataArray);
        setPayslipData(dataArray);
        // Extract unique years from data
        if (dataArray.length > 0) {
          const uniqueYears = [...new Set(dataArray.map(p => p.month_year.split('-')[0]))].sort((a, b) => b - a);
          setYears(uniqueYears.map(y => parseInt(y)));
        } else {
          const currentYear = dayjs().year();
          setYears([currentYear, currentYear - 1, currentYear - 2, currentYear - 3, currentYear - 4]);
        }
      }
    } catch (error) {
      console.error("Error loading payslips:", error);
      message.error("Failed to load payslips");
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = (record) => {
    setSelectedPayslip(record);
    setViewModalVisible(true);
  };

  const handleDownloadPayslip = async (record) => {
    try {
      message.loading({ content: 'Generating payslip...', key: 'download' });
      const response = await axios.get(`/payroll/payroll-histories/${record.id}/download_payslip/`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const monthYear = record.month_year.replace('-', '_');
      link.setAttribute('download', `payslip_${record.employee_code}_${monthYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      message.success({ content: 'Payslip downloaded successfully!', key: 'download' });
    } catch (error) {
      console.error("Error downloading payslip:", error);
      message.error({ content: 'Failed to download payslip', key: 'download' });
    }
  };

  const columns = [
    {
      title: 'Month/Year',
      dataIndex: 'month_year',
      key: 'month_year',
      width: 140,
      render: (text) => {
        if (!text) return "-";
        return dayjs(text, "YYYY-MM").format("MMMM YYYY");
      },
      sorter: (a, b) => dayjs(a.month_year, "YYYY-MM").unix() - dayjs(b.month_year, "YYYY-MM").unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const colorMap = {
          GENERATED: 'blue',
          APPROVED: 'green',
          PAID: 'purple',
          CANCELLED: 'red',
        };
        return (
          <Tag color={colorMap[status] || 'default'}>
            {status || 'GENERATED'}
          </Tag>
        );
      },
    },
    {
      title: 'Gross Salary',
      dataIndex: 'gross_salary',
      key: 'gross_salary',
      width: 130,
      align: 'right',
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'Deductions',
      dataIndex: 'total_deductions',
      key: 'total_deductions',
      width: 120,
      align: 'right',
      render: (amount) => amount ? `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '-',
    },
    {
      title: 'Net Salary',
      dataIndex: 'net_salary_rounded_off',
      key: 'net_salary_rounded_off',
      width: 130,
      align: 'right',
      render: (amount) => <strong>₹{parseFloat(amount || 0).toLocaleString('en-IN')}</strong>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewPayslip(record)}
          >
            View
          </Button>
          <Button
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleDownloadPayslip(record)}
          >
            Download
          </Button>
        </Space>
      ),
    },
  ];

  const renderEarningsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#1890ff' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDeductionsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#cf1322' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ height: '100vh', overflow: 'hidden' }}>
      <Card
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined />
            Payslips
          </div>
        }
        style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Space style={{ marginBottom: '16px' }} wrap>
          <Select
            value={selectedYear}
            style={{ width: 120 }}
            onChange={setSelectedYear}
            placeholder="Select Year"
          >
            <Option value="all">All Years</Option>
            {years.map(year => (
              <Option key={year} value={year}>{year}</Option>
            ))}
          </Select>
        </Space>

        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Spin spinning={loading}>
            {payslipData.length === 0 && !loading ? (
              <Empty description="No payslips found" />
            ) : (
              <Table
                columns={columns}
                dataSource={payslipData}
                rowKey="id"
                pagination={{
                  defaultPageSize: 12,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payslips`,
                }}
                scroll={{ x: 800, y: 400 }}
                size="middle"
              />
            )}
          </Spin>
        </div>
      </Card>

      <Modal
        title={
          selectedPayslip ?
            `Payslip - ${dayjs(selectedPayslip.month_year, "YYYY-MM").format("MMMM YYYY")}` :
            'Payslip Details'
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadPayslip(selectedPayslip)}
          >
            Download PDF
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPayslip && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p><strong>Employee Name:</strong> {selectedPayslip.employee_name}</p>
                <p><strong>Employee Code:</strong> {selectedPayslip.employee_code}</p>
                <p><strong>Department:</strong> {selectedPayslip.department_name || '-'}</p>
                <p><strong>Designation:</strong> {selectedPayslip.designation || '-'}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p><strong>Pay Period:</strong> {dayjs(selectedPayslip.month_year, "YYYY-MM").format("MMMM YYYY")}</p>
                <p><strong>Working Days:</strong> {selectedPayslip.total_working_days}</p>
                <p><strong>Effective Days:</strong> {selectedPayslip.effective_days}</p>
                <p><strong>Days Present:</strong> {selectedPayslip.is_attendance_considered ? parseFloat(selectedPayslip.days_present || 0).toFixed(1) : '-'}</p>
                <p><strong>Attendance Considered:</strong> {selectedPayslip.is_attendance_considered ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>}</p>
                <p><strong>Status:</strong> <Tag color={selectedPayslip.status === 'PAID' ? 'purple' : selectedPayslip.status === 'APPROVED' ? 'green' : 'blue'}>{selectedPayslip.status}</Tag></p>
              </div>
            </div>

            <Divider />

            <div style={{ display: 'flex', gap: 16 }}>
              <Card size="small" title="Earnings Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#f6ffed' }}>
                {renderEarningsBreakdown(selectedPayslip.earnings_breakdown, selectedPayslip.ytd_earnings_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Earnings:</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayslip.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedPayslip.ytd_total_earnings !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayslip.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>

              <Card size="small" title="Deductions Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#fff2e8' }}>
                {renderDeductionsBreakdown(selectedPayslip.deductions_breakdown, selectedPayslip.ytd_deductions_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Deductions:</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedPayslip.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedPayslip.ytd_total_deductions !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedPayslip.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>
            </div>

            <Divider />

            <Card size="small" style={{ backgroundColor: '#f0f9ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
                <span style={{ flex: 2 }}>Summary</span>
                <span style={{ flex: 1, textAlign: 'right' }}>This Month</span>
                {selectedPayslip.ytd_net_salary !== undefined && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Basic Salary:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayslip.basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayslip.ytd_basic_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayslip.ytd_basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Earnings:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayslip.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayslip.ytd_total_earnings !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayslip.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Deductions:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedPayslip.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayslip.ytd_total_deductions !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedPayslip.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold' }}>
                <span style={{ flex: 2 }}>Net Salary:</span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayslip.net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayslip.ytd_net_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#52c41a' }}>₹{parseFloat(selectedPayslip.ytd_net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginTop: 4 }}>
                <span style={{ flex: 2 }}><strong>Rounded Off:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff', fontWeight: 'bold' }}>₹{Math.round(parseFloat(selectedPayslip.net_salary || 0)).toLocaleString('en-IN')}</span>
                {selectedPayslip.ytd_net_salary !== undefined && <span style={{ flex: 1 }}></span>}
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmployeePayslips;
